import { NotificationDropdown } from 'modules/layouts/LoggedLayout/components/Topbar/subcomponents/NotificationDropdown';
import { useNotifications } from 'modules/notification/hooks/useNotifications';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton } from 'shared/components/IconButton/IconButton';
import { useApplicationSearchParam } from 'shared/hooks/useApplicationSearchParam';
import { useBasePathname } from 'shared/hooks/useBasePathname';
import { useBreakpoint } from 'shared/hooks/useBreakpoint';
import { useSitemap } from 'shared/hooks/useSitemap';
import { BellIcon, HamburgerIcon, HeartIcon } from 'shared/icons';
import { ConnectisLogo } from 'shared/icons/ConnectisLogo';
import { mergeClasses } from 'shared/utils/mergeClasses';

const pageTitleKeys: Record<string, string> = {
  '/': 'offers',
  '/applications': 'applications',
  '/profile': 'profile',
  '/settings': 'settings',
  '/notifications': 'offers',
  '/observed': 'observed',
  '/job-orders': 'offers',
};

const getPageTitleKey = (path: keyof typeof pageTitleKeys, isApplicationParamPresent: boolean) => {
  if (path === '/job-orders' && isApplicationParamPresent) {
    return 'applications';
  }
  return pageTitleKeys[path];
};

interface TopbarProps {
  topbarHidden: boolean;
  onHamburgerClick: () => void;
}

export const Topbar: FC<TopbarProps> = memo(({ onHamburgerClick, topbarHidden }) => {
  const navigate = useNavigate();
  const sitemap = useSitemap();
  const { t } = useTranslation();
  const basePathname = useBasePathname();
  const { notifications } = useNotifications();
  const { isApplicationParamPresent } = useApplicationSearchParam();
  const isMobile = useBreakpoint('md');

  const notificationToggleButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const unviewedNotificationCount = useMemo(
    () => notifications.filter(({ viewedAt }) => !viewedAt).length,
    [notifications],
  );

  const handleBellClick = useCallback(() => {
    if (isMobile) {
      navigate(sitemap.notifications);
      return;
    }
    setNotificationsOpen((prev) => !prev);
  }, [isMobile]);

  const topbarLabel = useMemo(
    () => getPageTitleKey(basePathname, isApplicationParamPresent),
    [basePathname, isApplicationParamPresent],
  );

  return (
    <>
      <NotificationDropdown
        buttonRef={notificationToggleButtonRef}
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
      <div
        className={mergeClasses(
          'topbar fixed z-40 flex w-full items-center justify-between overflow-hidden transition-all before:h-full after:right-0 lg:w-[calc(100dvw-85px-16px)] lg:rounded-b-2xl',
          topbarHidden ? '-top-25 lg:top-0' : 'top-0',
        )}
      >
        <h1 className="hidden text-base font-bold text-lightBlue lg:block">
          {t(`pageTitles:${topbarLabel}`)}
        </h1>
        <Link className="lg:hidden" to={sitemap.main}>
          <ConnectisLogo className="w-[120px]" />
        </Link>
        <div className="flex gap-4">
          <Link className="hidden lg:flex" to={sitemap.observed}>
            <IconButton name="observedRedirectButton" icon={HeartIcon} />
          </Link>
          <div className="relative">
            <IconButton
              ref={notificationToggleButtonRef}
              name="notificationsToggleButton"
              icon={BellIcon}
              ariaLabel={
                unviewedNotificationCount
                  ? t('notification:bellAriaLabelUnviewed', { count: unviewedNotificationCount })
                  : t('notification:bellAriaLabel')
              }
              onClick={handleBellClick}
            />
            {!!unviewedNotificationCount && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -right-1 -top-1 flex min-w-[20px] items-center justify-center rounded-xl bg-red p-0.5 text-xxs font-medium text-white"
              >
                {unviewedNotificationCount}
              </span>
            )}
          </div>
          <IconButton
            onClick={onHamburgerClick}
            className="flex lg:hidden"
            name="notificationsToggleButton"
            icon={HamburgerIcon}
          />
        </div>
      </div>
    </>
  );
});
